// THE shared email row (email-surface unification) — one implementation for the
// /inbox accordion and both AccountDetail email tabs. The pop-out keeps its own
// three-pane layout but shares useEmailActions/useEmailContent, so behavior is
// identical everywhere; only the shells differ.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Check,
  Forward,
  Loader2,
  Mail,
  MailOpen,
  MoreHorizontal,
  Paperclip,
  Pin,
  Reply,
  ReplyAll,
  Send,
  Star,
  StickyNote,
  Tag,
  UserPlus,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { EmailHtmlBody } from "./EmailHtmlBody";
import { api, type User } from "@/lib/api";
import { categoryColor, emailTaskStatusLabel, EMAIL_TASK_STATUSES, fullWhen, internalDeliveryBadge, shortWhen, statusChipClass } from "@/lib/email/format";
import { isPersonalRow } from "@/lib/email/personalMail";
import type { UnifiedEmail } from "@/lib/email/types";
import type { EmailActions } from "./useEmailActions";
import { EmailAttachmentList } from "./EmailAttachmentList";
import { useEmailBody } from "./useEmailContent";

export interface EmailAutomationBadge {
  automation_name: string;
  automation_detail?: string | null;
}

export interface EmailRowProps {
  email: UnifiedEmail;
  variant: "inbox" | "account-shared" | "account-contact";
  currentUserId: string;
  /** Assignable roster (contractors/portal clients excluded server-side). */
  users: User[];
  makershubForwardConfigured: boolean;
  knownCategories: string[];
  actions: EmailActions;
  onReply: (email: UnifiedEmail, opts?: { replyAll?: boolean }) => void;
  onForward: (email: UnifiedEmail) => void;
  onOpenClaimDialog: (email: UnifiedEmail) => void;
  /** Violet Zap badge for automation-touched emails (account surface). */
  automationEntry?: EmailAutomationBadge | null;
  /** Far-left Claim/Assigned pill (the account Shared Inbox tab's signature control). */
  prominentClaim?: boolean;
  /** Open the row on mount — used by the ?email= deep link from assignment emails. */
  initiallyExpanded?: boolean;
  /** False for contractors: hides Reply / Reply all / Forward on shared/client mail. */
  canSend?: boolean;
  /** Bulk multi-select (opt-in per surface). The checkbox is hover-revealed until something
   *  is selected, then pinned visible so the current selection can never be off-screen-only. */
  selectable?: boolean;
  selected?: boolean;
  /** True while ANY row on this surface is selected. */
  selectionActive?: boolean;
  onToggleSelect?: (shiftKey: boolean) => void;
}

export function EmailRow({
  email,
  variant,
  currentUserId,
  users,
  makershubForwardConfigured,
  knownCategories,
  actions,
  onReply,
  onForward,
  onOpenClaimDialog,
  automationEntry,
  prominentClaim,
  initiallyExpanded,
  canSend = true,
  selectable,
  selected,
  selectionActive,
  onToggleSelect,
}: EmailRowProps) {
  const [expanded, setExpanded] = useState(!!initiallyExpanded);
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [pinMenuOpen, setPinMenuOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const claimed = !!email.taskStatus || !!email.assignedToUserId;
  // ENG-101: the collapsed badge always shows the task STATUS, never the assignee name —
  // "Resolved" must be visible even when assigned, and the claim pill / expanded-row select
  // already carry the name. Legacy rows claimed before statuses existed (assignedToUserId,
  // no taskStatus) read as "Assigned".
  const chipStatus = email.taskStatus ?? "assigned";
  const chipLabel = emailTaskStatusLabel[chipStatus] ?? chipStatus;
  // ENG-98 + v1.5.69: "→ Jennifer +1" (routed in by a transport rule) or "Jennifer +1"
  // (staff also addressed directly) — label/tooltip math lives in the tested helper.
  const staffBadge = internalDeliveryBadge(email.originallyTo, email.internalRecipients);
  const flagged = email.flagStatus === "flagged";
  const busy = !!actions.busy[email.id];
  // "My Mail" personal rows: claim/pin/waiting-on/MakersHub-forward/save-as-note don't apply
  // (no client to attach them to, and the server rejects the write anyway — see routes.ts
  // rejectIfPersonalMailboxTarget). Reply/forward/read-toggle/categorize stay available.
  const isPersonal = isPersonalRow(email);
  // Contractors can't send as a shared mailbox; their own personal mail stays sendable.
  const sendAllowed = canSend || isPersonal;
  const senderName = email.fromName || email.fromAddress || "(unknown sender)";
  const categories = email.categories ?? [];

  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(assignSearch.trim().toLowerCase()),
  );

  // Body loads only once the row is expanded; the server marks the message read on this
  // fetch (W4), so expansion also patches the caches optimistically.
  const { data: body, isLoading: bodyLoading, isError: bodyError } = useEmailBody(email.mailbox, email.id, {
    enabled: expanded,
    fallback: email.snippet,
  });

  // Lazy project list for the Pin menu — active only (open/in_progress/on_hold); closed
  // occurrences of recurring work would otherwise flood the menu with identical titles.
  const { data: projects = [] } = useQuery({
    queryKey: ["account-projects", email.accountId],
    queryFn: () => api.getProjects({ accountId: email.accountId, status: "active" }),
    enabled: pinMenuOpen && !!email.accountId,
    staleTime: 60_000,
  });

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) actions.markReadLocal(email);
  };

  // Deep-linked rows (?email=) open without a click. The useState initializer covers a fresh
  // mount; this effect covers the row already being mounted when the link is followed from
  // the same page. Either way the body fetch marks the message read server-side, so patch the
  // caches exactly as toggleExpand does. Runs once so a manual collapse afterwards sticks.
  const deepLinkOpenedRef = useRef(false);
  useEffect(() => {
    if (initiallyExpanded && !deepLinkOpenedRef.current) {
      deepLinkOpenedRef.current = true;
      setExpanded(true);
      actions.markReadLocal(email);
    }
  }, [initiallyExpanded, email.id]);

  const toggleCategory = (name: string) => {
    const next = categories.includes(name) ? categories.filter((c) => c !== name) : [...categories, name];
    actions.setCategories(email, next);
  };

  const addNewLabel = () => {
    const name = newLabel.trim();
    if (!name) return;
    if (!categories.includes(name)) actions.setCategories(email, [...categories, name]);
    setNewLabel("");
  };

  return (
    <div className={cn("group/row border-b last:border-b-0", !email.isRead && "bg-blue-50/40 dark:bg-blue-950/20")}>
      {/* Collapsed two-line row */}
      <div className="flex items-start gap-2 px-3 py-2">
        {/* Multi-select checkbox. The WRAPPER owns the click so shiftKey is readable —
            Radix's onCheckedChange hands back a boolean, not the event, so range-select
            would be impossible from the control itself. */}
        {selectable && !isPersonal && (
          <div
            className={cn("mt-0.5 shrink-0", !(selected || selectionActive) && "opacity-0 group-hover/row:opacity-100")}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect?.(e.shiftKey);
            }}
            onMouseDown={(e) => {
              // Shift-click otherwise extends the browser's text selection across the list.
              if (e.shiftKey) e.preventDefault();
            }}
          >
            <Checkbox checked={!!selected} className="pointer-events-none" aria-label="Select email" />
          </div>
        )}

        <button onClick={toggleExpand} className="mt-0.5 text-muted-foreground hover:text-foreground" aria-label="Expand">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        {prominentClaim && !isPersonal && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            {claimed ? (
              <button
                className="flex items-center gap-1 whitespace-nowrap rounded-full bg-blue-100 px-2 py-1 text-[10px] font-semibold text-blue-700 transition-colors hover:bg-blue-200"
                title="Reassign email"
                onClick={() => onOpenClaimDialog(email)}
              >
                <UserPlus className="h-3 w-3 shrink-0" />
                {email.assignedToName?.split(" ")[0] ?? "Assigned"}
              </button>
            ) : (
              <button
                className="whitespace-nowrap rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-emerald-700"
                onClick={() => onOpenClaimDialog(email)}
              >
                Claim
              </button>
            )}
          </div>
        )}

        <div className="min-w-0 flex-1 cursor-pointer" onClick={toggleExpand}>
          {/* Line 1: subject + badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            {!email.isRead && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" title="Unread" />}
            {flagged && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />}
            <span className={cn("truncate text-sm", !email.isRead ? "font-semibold" : "font-medium")}>
              {email.subject || "(no subject)"}
            </span>
            {email.hasAttachments && <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            {categories.slice(0, 2).map((c) => (
              <span key={c} className={cn("shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium", categoryColor(c))}>
                {c}
              </span>
            ))}
            {categories.length > 2 && (
              <span className="shrink-0 text-[10px] text-muted-foreground">+{categories.length - 2}</span>
            )}
            {automationEntry && (
              <Badge
                variant="outline"
                className="shrink-0 gap-1 border-violet-300 text-[10px] text-violet-700"
                title={automationEntry.automation_detail ?? automationEntry.automation_name}
              >
                <Zap className="h-2.5 w-2.5" />
                {automationEntry.automation_name}
              </Badge>
            )}
            {claimed && (
              <Badge className={cn("shrink-0 text-[10px]", statusChipClass(chipStatus))} title={chipLabel}>
                {chipLabel}
              </Badge>
            )}
            {email.pinned && (
              <Badge variant="outline" className="shrink-0 gap-1 text-[10px]">
                <Pin className="h-2.5 w-2.5" />
                {email.pinnedProjectTitle || "Pinned"}
              </Badge>
            )}
            {variant === "account-contact" && email.folder === "sent" && (
              <Badge className="shrink-0 bg-sky-100 text-[10px] text-sky-700 hover:bg-sky-100">Sent</Badge>
            )}
            {/* ENG-98 + v1.5.69: routing/double-work badge — "→ Jennifer +1" means a
                transport-rule BCC copy (mailbox not addressed), "Jennifer +1" means staff
                were addressed alongside the shared mailbox (they hold their own copies).
                Shared surfaces only; the Emails tab (account-contact) keeps its via badges. */}
            {staffBadge && (variant === "inbox" || variant === "account-shared") && (
              <Badge
                variant="outline"
                className="shrink-0 border-amber-300 bg-amber-50 text-[10px] text-amber-800"
                title={staffBadge.tooltip}
              >
                {staffBadge.label}
              </Badge>
            )}
            {variant === "account-contact" && email.via === "contact-match" && (
              <Badge
                variant="outline"
                className={cn(
                  "shrink-0 text-[10px]",
                  email.isPersonalMailbox && "border-amber-300 bg-amber-50 text-amber-800",
                )}
                title={
                  email.isPersonalMailbox
                    ? "Surfaced from a staff personal inbox because it matches a client contact"
                    : "Surfaced by contact match"
                }
              >
                via {email.mailbox}
              </Badge>
            )}
          </div>
          {/* Line 2: sender + snippet */}
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="max-w-[200px] shrink-0 truncate">{senderName}</span>
            <span className="truncate">{email.snippet}</span>
          </div>
        </div>

        {/* Right: time + actions */}
        <div className="flex shrink-0 items-center gap-1">
          <span className="mr-1 text-[11px] text-muted-foreground" title={fullWhen(email.receivedDateTime)}>{shortWhen(email.receivedDateTime)}</span>

          {!isPersonal && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title={flagged ? "Remove from Waiting On" : "Add to Waiting On"}
              onClick={() => actions.toggleFlag(email, !flagged)}
            >
              <Star className={cn("h-4 w-4", flagged ? "fill-amber-400 text-amber-400" : "text-muted-foreground")} />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title={email.isRead ? "Mark unread" : "Mark read"}
            onClick={() => actions.setRead(email, !email.isRead)}
          >
            {email.isRead ? (
              <MailOpen className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Mail className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>

          {sendAllowed && (
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Reply" onClick={() => onReply(email)}>
              <Reply className="h-4 w-4 text-blue-600" />
            </Button>
          )}

          {/* Pin to a project — not applicable to a personal mailbox (no client to pin to) */}
          {!isPersonal && (
            <DropdownMenu open={pinMenuOpen} onOpenChange={setPinMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Pin to project" disabled={busy}>
                  <Pin className={cn("h-4 w-4", email.pinned ? "text-amber-600" : "text-muted-foreground")} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72 w-64 overflow-auto">
                <DropdownMenuLabel>Pin to project</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {projects.length === 0 ? (
                  <DropdownMenuItem disabled>No active projects</DropdownMenuItem>
                ) : (
                  projects.map((p: any) => {
                    // Period label (server-computed period_display) separates same-titled
                    // occurrences; passing it as the title keeps the optimistic chip/toast
                    // consistent with what the server overlay returns on the next load.
                    const label = p.period_display ? `${p.title} — ${p.period_display}` : p.title;
                    return (
                      <DropdownMenuItem key={p.id} onClick={() => actions.pinToProject(email, { id: p.id, title: label })}>
                        {label}
                      </DropdownMenuItem>
                    );
                  })
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Claim / assign / status — not applicable to a personal mailbox */}
          {!isPersonal && (
            <DropdownMenu
              open={assignMenuOpen}
              onOpenChange={(open) => {
                setAssignMenuOpen(open);
                if (!open) setAssignSearch("");
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Claim / assign" disabled={busy}>
                  <UserPlus className={cn("h-4 w-4", claimed ? "text-violet-600" : "text-muted-foreground")} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Assign to</DropdownMenuLabel>
                <div className="px-2 pb-1.5">
                  <Input
                    autoFocus
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Search staff…"
                    className="h-7 text-xs"
                  />
                </div>
                <DropdownMenuSeparator />
                <div className="max-h-56 overflow-auto">
                  {filteredUsers.length === 0 ? (
                    <DropdownMenuItem disabled>No matching staff</DropdownMenuItem>
                  ) : (
                    filteredUsers.map((u) => (
                      <DropdownMenuItem key={u.id} onClick={() => actions.claim(email, { id: u.id, name: u.name })} className="max-w-full">
                        {u.id === email.assignedToUserId && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                        <span className="truncate">{u.name}</span>
                        {u.id === currentUserId && <span className="ml-1 shrink-0 text-xs text-muted-foreground">(me)</span>}
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onOpenClaimDialog(email)}>
                  {claimed ? "Reassign with notes…" : "Claim with notes…"}
                </DropdownMenuItem>
                {claimed && <DropdownMenuItem onClick={() => actions.claim(email, null)}>Unassign</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Status</DropdownMenuLabel>
                {EMAIL_TASK_STATUSES.map((s) => (
                  <DropdownMenuItem key={s} onClick={() => actions.setStatus(email, s)}>
                    {email.taskStatus === s && <Check className="mr-2 h-3.5 w-3.5 shrink-0" />}
                    {emailTaskStatusLabel[s]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* More: reply-all / forward / categorize */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" title="More actions">
                <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {sendAllowed && (
                <DropdownMenuItem onClick={() => onReply(email, { replyAll: true })}>
                  <ReplyAll className="mr-2 h-4 w-4" /> Reply all
                </DropdownMenuItem>
              )}
              {sendAllowed && (
                <DropdownMenuItem onClick={() => onForward(email)}>
                  <Forward className="mr-2 h-4 w-4" /> Forward…
                </DropdownMenuItem>
              )}
              {/* accountId is always present on a shared/client-mailbox row (My Mail's is "" —
                  no client to attach a note to, so this item is hidden for personal rows). */}
              {!isPersonal && (
                <DropdownMenuItem onClick={() => actions.saveAsNote(email)} disabled={busy}>
                  <StickyNote className="mr-2 h-4 w-4" /> Save as client note
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Tag className="mr-2 h-4 w-4" /> Categorize
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-72 w-56 overflow-auto">
                  {knownCategories.length === 0 && (
                    <DropdownMenuItem disabled>No labels yet</DropdownMenuItem>
                  )}
                  {knownCategories.map((name) => (
                    <DropdownMenuCheckboxItem
                      key={name}
                      checked={categories.includes(name)}
                      onCheckedChange={() => toggleCategory(name)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <span className={cn("mr-1.5 inline-block h-2.5 w-2.5 rounded-full", categoryColor(name).split(" ")[0])} />
                      {name}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <div className="flex items-center gap-1 px-2 py-1.5">
                    <Input
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") addNewLabel();
                      }}
                      placeholder="New label…"
                      className="h-7 text-xs"
                    />
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addNewLabel} disabled={!newLabel.trim()}>
                      Add
                    </Button>
                  </div>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Forward invoice to MakersHub (only when the client has a forward address; not applicable to a personal mailbox) */}
          {!isPersonal && makershubForwardConfigured && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-amber-700"
              title="Forward invoice to MakersHub"
              onClick={() => actions.forwardToMakersHub(email)}
              disabled={busy}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              MH
            </Button>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-10 pb-3">
          {/* Recipients line (archived rows now carry to/cc — Phase A fill) */}
          {(email.toRecipients?.length ?? 0) > 0 && (
            <p className="mb-1.5 truncate text-xs text-muted-foreground">
              To: {email.toRecipients!.map((r) => r.name || r.address).join(", ")}
              {(email.ccRecipients?.length ?? 0) > 0 &&
                ` · Cc: ${email.ccRecipients!.map((r) => r.name || r.address).join(", ")}`}
            </p>
          )}

          {/* Status + assignee controls */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <select
              className="cursor-pointer rounded-md border bg-background px-2 py-1 text-xs"
              value={email.taskStatus ?? ""}
              onChange={(e) => e.target.value && actions.setStatus(email, e.target.value)}
            >
              <option value="" disabled>Set status…</option>
              {EMAIL_TASK_STATUSES.map((s) => (
                <option key={s} value={s}>{emailTaskStatusLabel[s]}</option>
              ))}
            </select>
            <select
              className="max-w-[220px] cursor-pointer rounded-md border bg-background px-2 py-1 text-xs"
              value={email.assignedToUserId ?? ""}
              onChange={(e) => {
                const u = users.find((x) => x.id === e.target.value);
                actions.claim(email, u ? { id: u.id, name: u.name } : null);
              }}
            >
              <option value="">Unassigned</option>
              {email.assignedToUserId && !users.some((u) => u.id === email.assignedToUserId) && (
                <option value={email.assignedToUserId}>{email.assignedToName ?? "(former assignee)"}</option>
              )}
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
            {email.taskNotes && (
              <span className="truncate text-xs italic text-muted-foreground" title={email.taskNotes}>
                📝 {email.taskNotes}
              </span>
            )}
          </div>

          {bodyLoading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading message…
            </div>
          ) : bodyError ? (
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm italic text-muted-foreground">
              {email.snippet || "(could not load message)"}
            </pre>
          ) : body ? (
            body.contentType === "html" ? (
              <EmailHtmlBody
                className="prose prose-sm max-w-none overflow-auto max-h-96 rounded border p-3"
                html={body.content}
              />
            ) : (
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm text-muted-foreground">
                {body.content}
              </pre>
            )
          ) : null}

          <EmailAttachmentList email={email} actions={actions} />
        </div>
      )}
    </div>
  );
}
